import {
	Button,
	ButtonGroup,
	Dialog,
	IconButton,
	Input,
	LuIcon,
	radiusCva,
	Separator,
	stopPropagation,
	Text,
	useProvider,
} from "@overmap-ai/blocks"
import { useControlledState } from "@overmap-ai/hooks"
import { cx } from "class-variance-authority"
import { type ChangeEventHandler, type FormEventHandler, memo, useCallback, useState } from "react"
import { twMerge } from "tailwind-merge"

import { modelIconMapping } from "../../mappings/icons"
import type { ExtendableDialogWithTitleProps } from "./typings"

interface MultiStringDialogProps extends Omit<ExtendableDialogWithTitleProps, "onSubmit"> {
	placeholder?: string
	initialValues?: string[]
	values?: string[]
	onValuesChange?: (values: string[]) => void
	onSubmit?: (values: string[]) => void
}

export const MultiStringDialog = memo<MultiStringDialogProps>((props) => {
	const {
		children,
		open,
		defaultOpen,
		onOpenChange,
		modal,
		title,
		initialValues,
		values: controlledValues,
		onValuesChange,
		placeholder,
		onSubmit,
		...rest
	} = props

	const [inputValue, setInputValue] = useState<string>("")
	const { radius } = useProvider()

	const [values, setValues] = useControlledState<string[]>(initialValues ?? [], controlledValues, onValuesChange)

	const handleChange = useCallback<ChangeEventHandler<HTMLInputElement>>((e) => {
		setInputValue(e.target.value)
	}, [])

	const handleInputSubmit = useCallback<FormEventHandler<HTMLFormElement>>(
		(e) => {
			e.preventDefault()
			e.stopPropagation()

			const trimmedValue = inputValue.trim()
			if (!trimmedValue || values.includes(trimmedValue)) return

			setValues([...values, trimmedValue])
			setInputValue("")
		},
		[inputValue, setValues, values],
	)

	const handleSubmit = useCallback(() => {
		onSubmit?.(values)
	}, [onSubmit, values])

	return (
		<Dialog.Root open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange} modal={modal}>
			{!!children && <Dialog.Trigger asChild>{children}</Dialog.Trigger>}
			<Dialog.Overlay onClick={stopPropagation} />
			<Dialog.Content title={title} onClick={stopPropagation} className="flex flex-col gap-4" {...rest}>
				<div className="flex justify-between gap-2">
					<Dialog.Title>
						<Text size="lg">{title}</Text>
					</Dialog.Title>

					<Dialog.Close asChild>
						<IconButton variant="ghost" accentColor="base">
							<LuIcon icon={modelIconMapping.close} />
						</IconButton>
					</Dialog.Close>
				</div>
				<form className="flex w-full gap-2" onSubmit={handleInputSubmit}>
					<Input.Root variant="outline" className="grow">
						<Input.Field placeholder={placeholder} value={inputValue} onChange={handleChange} type="text" />
					</Input.Root>
					<IconButton variant="soft" accentColor="base" type="submit">
						<LuIcon icon={modelIconMapping.add} />
					</IconButton>
				</form>
				{values.length > 0 && (
					<ol className="flex w-full grow flex-col gap-0.5 overflow-y-auto">
						{values.map((value, index) => (
							<li
								key={`${value}-${index}`}
								className={twMerge(
									cx(
										"bg-(--base-a3) flex h-8 w-full items-center justify-between gap-2 px-4",
										radiusCva({ radius, maxLarge: true }),
									),
								)}
							>
								<span className="truncate">{value}</span>
								<IconButton
									aria-label="remove"
									variant="ghost"
									size="sm"
									accentColor="base"
									onClick={() => {
										const newValues = [...values]
										newValues.splice(index, 1)
										setValues(newValues)
									}}
								>
									<LuIcon icon={modelIconMapping.close} />
								</IconButton>
							</li>
						))}
					</ol>
				)}
				<Separator size="full" />
				<ButtonGroup className="flex grow justify-end gap-2" size="sm">
					<Dialog.Close asChild>
						<Button accentColor="base" variant="soft" type="button">
							Cancel
						</Button>
					</Dialog.Close>
					<Dialog.Close asChild>
						<Button onClick={handleSubmit} variant="surface">
							Apply
						</Button>
					</Dialog.Close>
				</ButtonGroup>
			</Dialog.Content>
		</Dialog.Root>
	)
})
MultiStringDialog.displayName = "MultiStringDialog"
